import styled from "@emotion/styled";
import {
  borderRadius,
  spacing,
  controlFontSize,
  brand,
  text,
} from "@atlas-int/tokens";
import { TradeDirection } from "@atlas-int/data/types";
import { worldGroupDatum, allProductsDatum, UIView, VizType } from "@atlas-int/data";
import { Button } from "@atlas-int/ui";
import { useExploreParams } from "../../visualization/paramSchemas";

interface Props {
  uiViewOption: UIView;
  selected: boolean;
  vizType: VizType | undefined;
}

const SelectorButton = styled(Button)<{ selected?: boolean }>`
  flex-grow: 1;
  flex-basis: 0px;
  height: 100%;
  border-radius: ${borderRadius.pill};
  font-size: ${controlFontSize.default};
  padding: ${spacing[1]} ${spacing[6]};
  flex-direction: column;

  ${({ selected }) =>
    selected &&
    `
    background-color: ${brand.dark};
    color: ${text.onDark};

    &:hover:not(:disabled) {
      background-color: ${brand.dark};
    }
  `}

  &:disabled {
    background: none;
  }
`;

export default function UIViewSelectorButton({
  uiViewOption,
  selected,
  vizType,
}: Props) {
  const [{ yAxis, importer, exporter, tradeDirection }, setQuery] = useExploreParams();

  let buttonLabel: string | undefined;
  let changeAction: any;
  let disabled: boolean = false;
  if (uiViewOption === UIView.Markets) {
    buttonLabel = "Locations";
    changeAction = () => {
      // When switching to Markets view, one location MUST be World
      const isWorldExporter = exporter === worldGroupDatum.groupId;
      const isWorldImporter = importer === worldGroupDatum.groupId;

      let finalExporter = exporter;
      let finalImporter = importer;
      let newTradeDirection = tradeDirection;

      // If both are non-world, force importer to world
      if (!isWorldExporter && !isWorldImporter) {
        finalImporter = worldGroupDatum.groupId;
        newTradeDirection = TradeDirection.Exports;
      }
      // If exporter is world and importer is specific
      else if (isWorldExporter && !isWorldImporter) {
        newTradeDirection = TradeDirection.Imports;
      }
      // If importer is world and exporter is specific
      else if (!isWorldExporter && isWorldImporter) {
        newTradeDirection = TradeDirection.Exports;
      }
      // If both are world
      else if (isWorldExporter && isWorldImporter) {
        newTradeDirection = tradeDirection || TradeDirection.Exports;
      }

      setQuery({
        view: UIView.Markets,
        exporter: finalExporter,
        importer: finalImporter,
        product: allProductsDatum.productId,
        tradeDirection: newTradeDirection,
        //reset to default
        colorBy: undefined,
        //per capita not available for markets view
        yAxis: yAxis?.includes("Capita") ? undefined : yAxis,
      });
    };
  } else if (uiViewOption === UIView.Products) {
    buttonLabel = "Products";
    changeAction = () => {
      const isWorldExporter = exporter === worldGroupDatum.groupId;
      const isWorldImporter = importer === worldGroupDatum.groupId;

      // For Products view, determine tradeDirection based on positions
      let newTradeDirection: TradeDirection;

      if (isWorldExporter && isWorldImporter) {
        // Both World: keep current direction or default to Exports (ambiguous case)
        newTradeDirection = tradeDirection || TradeDirection.Exports;
      } else if (isWorldExporter) {
        // World → Country: imports
        newTradeDirection = TradeDirection.Imports;
      } else {
        // Country → World or Country → Country: exports
        newTradeDirection = TradeDirection.Exports;
      }

      setQuery({
        view: UIView.Products,
        product: allProductsDatum.productId,
        tradeDirection: newTradeDirection,
      });
    };
    if (vizType === VizType.Geo) {
      // If on Geomap, disable Product view selector because not relevant
      disabled = true;
    }
  }

  return (
    <SelectorButton
      variant="selector"
      selected={selected}
      disabled={disabled}
      onClick={() => changeAction?.()}
    >
      {buttonLabel}
    </SelectorButton>
  );
}
